import { Subject } from "rxjs";
import { tap, debounceTime } from 'rxjs/operators';

export declare type FilterOperator = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'startswith' | 'endswith' | 'contains';

export declare type FilterPropertyType = 'string' | 'integer';

export declare type SortDirection = 'asc' | 'desc';


export interface FetchDataResult<T> {
    data?: T[];
    itemsCount?: number;
}

export interface FetchErrorResult {
    errorMsg: string;
}


export interface FilterModel {
    field: string;
    operator: FilterOperator;
    type?: FilterPropertyType;
    value: string | number | Date | boolean;
}

export interface SortModel {
    field: string;
    direction?: SortDirection;
}


export interface DataSourceState<T> {
    loading: boolean;
    data?: T[] | null;
    hasError: boolean;
    errorMsg?: string;
    currentPage?: number;
    pageSize?: number;
    itemsCount?: number;
    sortedItems: { [index: string]: SortModel };
    filteredItems: { [index: string]: FilterModel };
}

interface FilterEv {
    field: string;
    value?: string;
}


export abstract class DataSource<T> {

    protected state: DataSourceState<T>;
    public state$: Subject<DataSourceState<T>>;
    public filter$: Subject<FilterEv>;

    constructor() {

        this.state = {
            loading: true,
            data: undefined,
            hasError: false,
            currentPage: 0,
            pageSize: undefined,
            itemsCount: 0,
            sortedItems: {},
            filteredItems: {}
        };
        this.state$ = new Subject();
        this.filter$ = new Subject();
        this.filter$.pipe(
            tap(this.filterUpdate),
            debounceTime(300)
        ).subscribe(this.filterSubscribe);
    }

    protected abstract fetchData(): void;

    private filterUpdate = (e: FilterEv) => {
        console.log("ON-UPDATE: ", e.field, e.value);
    }

    private filterSubscribe = (e: FilterEv) => {
        console.log("ON-FILTER: ", e.field, e.value);
        this.state.filteredItems[e.field].value = e.value;
        this.setState({ ...this.state, currentPage: 0 });
        this.fetch();
    }

    protected setState = (state: object): void => {
        this.state = Object.assign(this.state, state);
        this.state$.next(this.state);
    }

    public getState = (): DataSourceState<T> => {
        return this.state;
    }

    public setCurrentPage = (newPage: number): void => {

        if (this.state.currentPage !== newPage) {
            this.setState({ currentPage: newPage });
            this.fetch();
        }
    }

    public setSortedItems = (sortedItems: { [index: string]: SortModel }) => {
        this.state.sortedItems = sortedItems;
    }

    public setFilteredItems = (filteredItems: { [index: string]: FilterModel }) => {
        this.state.filteredItems = filteredItems;
    }

    public setPageSize = (newPageSize: number, withoutFetch?: boolean): void => {

        if (this.state.pageSize !== newPageSize) {
            this.setState({ currentPage: 0, pageSize: newPageSize });
            if (withoutFetch !== true) {
                this.fetch();
            }
        }
    }

    public onSort = (sort?: SortModel, reset?: boolean): void => {

        const newSortedItems: { [index: string]: SortModel } = (reset === false) ? { ...this.state.sortedItems } : {};

        if (sort !== undefined) {
            newSortedItems[sort.field] = { field: sort.field, direction: sort.direction === 'desc' ? 'desc' : 'asc' };
        }

        this.setState({ ...this.state, currentPage: 0, sortedItems: newSortedItems });
        this.fetch();
    };

    public onFilter = (filterItems: { [index: string]: FilterModel }): void => {
        this.setState({ ...this.state, currentPage: 0, filteredItems: filterItems });
        this.fetch();
        //this.filter$.next({ field: field, value: value });
    }

    private loadingState = { loading: true, hasError: false, errorMsg: <string>undefined };
    private loadedState = { loading: false };

    protected fetchSuccess = (result: FetchDataResult<T>) => {
        const newState = { ...this.loadedState, data: result.data, itemsCount: result.itemsCount };
        this.setState(newState);
    }

    protected fetchError = (error: FetchErrorResult) => {
        const newState = { ...this.loadedState, data: <T>undefined, hasError: true, errorMsg: error.errorMsg };
        this.setState({ ...this.state, ...newState });
    }

    public fetch = (): void => {

        this.setState(this.loadingState);

        this.fetchData();
    }
}

export default DataSource;